import React from 'react';

import { Translate, TranslateHtml } from '@change-corgi/core/react/i18n';
import { Link } from '@change-corgi/design-system-legacy/components/actions';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { iconSecurity } from '@change-corgi/design-system-legacy/icons';
import { Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { PolicyInfoProps } from './hooks/usePolicyInfo';
import { usePolicyInfo } from './hooks/usePolicyInfo';

const sunriseLinkStyles = {
	color: 'sunrise.semantic.typography.interactive.default',
	'&:hover': {
		color: 'sunrise.semantic.typography.interactive.hover',
	},
	'&:focus': {
		color: 'sunrise.semantic.typography.interactive.pressed',
	},
	'&:active': {
		color: 'sunrise.semantic.typography.interactive.visited',
	},
};

export function PolicyInfo(props: PolicyInfoProps): React.JSX.Element {
	const { gateway, gatewayLink, privacyPolicyLink, termsOfServiceLink, termsOfServiceLinkText, isSunriseEnabled } =
		usePolicyInfo(props);

	return (
		<Flex sx={{ alignItems: 'center', flexDirection: 'row' }}>
			<Icon
				icon={iconSecurity}
				color="secondary-blue"
				size="xxl"
				{...(isSunriseEnabled && {
					color: 'sunrise.semantic.icon.strong',
				})}
			/>
			<Text
				as="span"
				size="caption"
				sx={{
					color: 'neutral-grey600',
					marginLeft: 16,
					...(isSunriseEnabled && {
						color: 'sunrise.semantic.typography.weak',
					}),
				}}
			>
				<TranslateHtml
					value="corgi.payments.policy_info.text"
					replacements={{
						gateway,
						gatewayLink: (
							<Link
								to={gatewayLink}
								sx={{
									color: 'neutral-grey600',
									fontWeight: 'normal',
									...(isSunriseEnabled && sunriseLinkStyles),
								}}
								target="_blank"
								tabIndex={0} // see accessibility.md
							>
								<Translate value="corgi.payments.policy_info.their_terms" />
							</Link>
						),
						privacyPolicyLink: (
							<Link
								to={privacyPolicyLink}
								sx={{
									color: 'neutral-grey600',
									fontWeight: 'normal',
									...(isSunriseEnabled && sunriseLinkStyles),
								}}
								target="_blank"
								tabIndex={0} // see accessibility.md
							>
								<Translate value="corgi.payments.policy_info.privacy_policy" />
							</Link>
						),
						termsOfServiceLink: (
							<Link
								to={termsOfServiceLink}
								sx={{
									color: 'neutral-grey600',
									fontWeight: 'normal',
									...(isSunriseEnabled && sunriseLinkStyles),
								}}
								target="_blank"
								tabIndex={0} // see accessibility.md
							>
								<Translate value={termsOfServiceLinkText} />
							</Link>
						),
					}}
				/>
			</Text>
		</Flex>
	);
}
